import { useCallback, useEffect } from 'react';

import {
  EllipticalROITool,
  LengthTool,
  PlanarFreehandROITool,
  ProbeTool,
  RectangleROITool,
  ToolGroupManager,
} from '@cornerstonejs/tools';
import { Events } from '@cornerstonejs/tools/dist/esm/enums';
import { getAnnotations } from '@cornerstonejs/tools/dist/esm/stateManagement/annotation/annotationState';
import { useAtomValue, useSetAtom } from 'jotai';

import { toolGroupId } from '../../../utils/cornerstone/initializeCornerstone';
import { mixpanelEvents, useTrackEvent } from '../../../utils/mixpanel';
import { type RblForToothSide } from '../../../utils/rbl';
import { selectTeethAtom } from '../../patientList/atoms';
import { modelsTogglesAtom } from '../findingSidebar/atoms';
import { useIsWithinConfidenceThreshold } from '../findingSidebar/confidenceScoreFilter/helpers';
import type { BoundingBoxFinding, ImageObject } from '../types';

import {
  type TFindingBounds,
  annotateActionAtom,
  hiddenAnnotationsAtom,
  selectedAnnotationAtom,
  selectedFindingsAtom,
} from './atoms';

export const isMatchingRBLPoint = (
  rblPoints?: RblForToothSide,
  selectedFinding?: TFindingBounds,
) => {
  if (!rblPoints || !selectedFinding) {
    return false;
  }

  if (
    selectedFinding.type === 'rbl' &&
    (selectedFinding.x === rblPoints.xAbd ||
      selectedFinding.x === rblPoints.xCej) &&
    (selectedFinding.y === rblPoints.yAbd ||
      selectedFinding.y === rblPoints.yCej)
  ) {
    return 'line';
  }

  if (
    selectedFinding.type === 'rbl' &&
    selectedFinding.x === rblPoints.xRoot &&
    selectedFinding.y === rblPoints.yRoot
  ) {
    return 'root';
  }

  return false;
};

export const sortByTooth = (
  toothA?: string | number,
  toothB?: string | number,
) => {
  if (Number(toothA) && Number(toothB)) {
    return Number(toothA) > Number(toothB) ? 1 : -1;
  }
  if (Number(toothA) && !Number(toothB)) {
    return 1;
  }
  if (Number(toothB) && !Number(toothA)) {
    return -1;
  }
  return String(toothA) > String(toothB) ? 1 : -1;
};

const getTeethArrayFromImage = (image?: ImageObject) => {
  return (image?.models?.tooth_identification?.findings ?? [])
    .filter((tooth) => tooth.toothNumber && tooth.toothNumber !== 'missing')
    .map((toothFinding) => toothFinding.toothNumber);
};

export const getDisplayToothNumbers = (
  currentImage: ImageObject,
  displayToothNumber: (toothNumber: string | number) => string,
  selectedPhoto?: ImageObject,
) => {
  const currentImageTeeth = getTeethArrayFromImage(currentImage);
  const isSelectedImageDentalPhoto =
    selectedPhoto?.detectedImageType === 'COLOR_IMAGE';
  const selectedImageTeeth = isSelectedImageDentalPhoto
    ? []
    : getTeethArrayFromImage(selectedPhoto);

  // Filter for unique tooth numbers
  const toothNumbers = [...currentImageTeeth, ...selectedImageTeeth].filter(
    (toothNumber, i, self) => self.indexOf(toothNumber) === i,
  );

  return toothNumbers
    .sort(sortByTooth)
    .map((toothNumber, i) =>
      i < toothNumbers.length - 1
        ? `${displayToothNumber(toothNumber)}, `
        : `${displayToothNumber(toothNumber)}`,
    );
};

export const useGetCariesFindings = ({ image }: { image: ImageObject }) => {
  const isWithinConfidenceThreshold = useIsWithinConfidenceThreshold();
  const enabledFindingTypes = useAtomValue(modelsTogglesAtom);
  const selectedFinding = useAtomValue(selectedFindingsAtom);
  const selectedTeeth = useAtomValue(selectTeethAtom);
  const hiddenFindings = useAtomValue(hiddenAnnotationsAtom);
  const hiddenCariesFindings = image?.models?.caries?.findings?.filter(
    (finding) => hiddenFindings.some((hidden) => hidden === finding.id),
  );

  const cariesFindings = image?.models?.caries?.findings
    ?.filter(
      (finding) =>
        finding?.toothNumber &&
        finding.toothNumber !== 'missing' &&
        finding.userValidation !== 'Disagree' &&
        isWithinConfidenceThreshold(
          'caries',
          finding.confidenceScore,
          finding?.bin,
        ) &&
        ((enabledFindingTypes.caries && finding.depth !== 'into_enamel') ||
          (enabledFindingTypes.incipients &&
            finding.depth === 'into_enamel')) &&
        (!selectedFinding || selectedFinding?.id === finding?.id) &&
        !hiddenCariesFindings?.some(
          (hiddenFinding) => hiddenFinding.id === finding.id,
        ),
    )
    .map((filteredFinding) => filteredFinding?.toothNumber);

  const initCariesFindingsByTooth: {
    tooth?: string | number;
    lesions: number;
  }[] = [];

  const cariesFindingsByTooth = cariesFindings
    ?.reduce((byTooth, finding) => {
      const tooth = byTooth.find((x) => x?.tooth === finding);
      tooth ? tooth.lesions++ : byTooth.push({ tooth: finding, lesions: 1 });
      return byTooth;
    }, initCariesFindingsByTooth)
    .filter(
      (tooth) =>
        !selectedTeeth.length ||
        selectedFinding ||
        selectedTeeth.some((x) => x === tooth.tooth?.toString()),
    )
    .sort((a, b) => sortByTooth(a.tooth, b.tooth));

  return cariesFindingsByTooth;
};

export const useGetCalculusFindings = ({ image }: { image: ImageObject }) => {
  const isWithinConfidenceThreshold = useIsWithinConfidenceThreshold();
  const enabledFindingTypes = useAtomValue(modelsTogglesAtom);
  const selectedFinding = useAtomValue(selectedFindingsAtom);
  const selectedTeeth = useAtomValue(selectTeethAtom);

  const hiddenFindings = useAtomValue(hiddenAnnotationsAtom);
  const hideCalculusFindings = hiddenFindings.some(
    (finding) => finding.split(',')?.length > 4,
  );

  const calcFindingsByToothUnsorted:
    | { tooth: string | number | undefined }[]
    | undefined = image?.models?.calculus?.findings
    ?.filter(
      (finding: BoundingBoxFinding) =>
        finding?.toothNumber &&
        finding.toothNumber !== 'missing' &&
        finding.userValidation !== 'Disagree' &&
        isWithinConfidenceThreshold(
          'calculus',
          finding.confidenceScore,
          finding?.bin,
        ) &&
        enabledFindingTypes.calculus &&
        (!selectedFinding || selectedFinding?.id === finding?.id) &&
        !hideCalculusFindings,
    )
    .map((filteredFinding: BoundingBoxFinding) => {
      return { tooth: filteredFinding?.toothNumber };
    });
  const calcFindingsByTooth = calcFindingsByToothUnsorted
    ?.filter(
      (tooth) =>
        !selectedTeeth.length ||
        selectedFinding ||
        selectedTeeth.some((x) => x === tooth.tooth?.toString()),
    )
    .sort((a, b) => sortByTooth(a.tooth, b.tooth));
  return calcFindingsByTooth;
};

export const useGetRblFindings = ({ image }: { image: ImageObject }) => {
  const enabledFindingTypes = useAtomValue(modelsTogglesAtom);
  const selectedFinding = useAtomValue(selectedFindingsAtom);
  const selectedTeeth = useAtomValue(selectTeethAtom);
  const hiddenFindings = useAtomValue(hiddenAnnotationsAtom);
  const hiddenRblFindings = hiddenFindings?.filter(
    (hidden) => hidden?.split(',')?.length === 4,
  );

  const rblFindingsByTooth = image?.models?.radiographic_boneloss?.findings
    ?.filter(
      (finding) =>
        finding?.toothNumber &&
        finding.toothNumber !== 'missing' &&
        enabledFindingTypes.radiographic_boneloss &&
        (!selectedFinding || selectedFinding.type === 'rbl'),
    )
    .map((rblByTooth) => {
      const toothNumber = rblByTooth.toothNumber;
      const rblSide = rblByTooth?.sides
        ?.filter(
          (rbl) =>
            rbl?.userValidation !== 'Disagree' &&
            rbl?.side !== 'center' &&
            (!selectedFinding ||
              rbl?.points.some(
                (point) =>
                  point.id === selectedFinding.id && point.rblType !== 'root',
              )) &&
            !hiddenRblFindings?.some(
              (hiddenRbl) =>
                rbl?.points
                  ?.filter((rblPoint) => rblPoint.rblType !== 'root')
                  ?.map((point) => [point.x, point.y])
                  .flat()
                  .join() === hiddenRbl,
            ),
        )
        .map((rblFinding) => {
          return {
            side: rblFinding.side,
            distance: rblFinding.distanceMM,
          };
        });
      return { tooth: toothNumber, rblSides: rblSide };
    })
    .filter(
      (tooth) =>
        tooth.rblSides.length &&
        tooth.rblSides?.some(
          (side) =>
            (side.side === 'mesial' || side.side === 'distal') && side.distance,
        ),
    )
    .filter(
      (findingTooth) =>
        !selectedTeeth.length ||
        selectedFinding ||
        selectedTeeth.some((x) => x === findingTooth.tooth?.toString()),
    )
    .sort((a, b) => sortByTooth(a.tooth, b.tooth));

  return rblFindingsByTooth;
};

export const useGetPrlFindings = ({ image }: { image: ImageObject }) => {
  const isWithinConfidenceThreshold = useIsWithinConfidenceThreshold();
  const enabledFindingTypes = useAtomValue(modelsTogglesAtom);
  const selectedFinding = useAtomValue(selectedFindingsAtom);
  const selectedTeeth = useAtomValue(selectTeethAtom);
  const hiddenFindings = useAtomValue(hiddenAnnotationsAtom);

  const prlFindingsByTooth = image?.models?.periapical_radiolucency?.findings
    ?.filter(
      (finding: BoundingBoxFinding) =>
        finding?.toothNumber &&
        finding.toothNumber !== 'missing' &&
        finding.userValidation !== 'Disagree' &&
        isWithinConfidenceThreshold(
          'periapical_radiolucency',
          finding.confidenceScore,
          finding?.bin,
        ) &&
        enabledFindingTypes.periapical_radiolucency &&
        (!selectedFinding || selectedFinding?.id === finding?.id) &&
        !hiddenFindings?.some((hidden) => hidden === finding.id),
    )
    .map((filteredFinding: BoundingBoxFinding) => {
      return { tooth: filteredFinding?.toothNumber };
    })
    .filter(
      (tooth) =>
        !selectedTeeth.length ||
        selectedFinding ||
        selectedTeeth.some((x) => x === tooth.tooth?.toString()),
    )
    .sort((a, b) => sortByTooth(a.tooth, b.tooth));
  return prlFindingsByTooth;
};

export const useGetRalFindings = ({ image }: { image: ImageObject }) => {
  const isWithinConfidenceThreshold = useIsWithinConfidenceThreshold();
  const enabledFindingTypes = useAtomValue(modelsTogglesAtom);
  const selectedFinding = useAtomValue(selectedFindingsAtom);
  const selectedTeeth = useAtomValue(selectTeethAtom);
  const hiddenFindings = useAtomValue(hiddenAnnotationsAtom);

  const ralFindings = image?.models?.restorative_adjacent_lucency?.findings
    ?.filter(
      (finding: BoundingBoxFinding) =>
        finding?.toothNumber &&
        finding.toothNumber !== 'missing' &&
        finding.userValidation !== 'Disagree' &&
        isWithinConfidenceThreshold(
          'restorative_adjacent_lucency',
          finding.confidenceScore,
          finding?.bin,
        ) &&
        enabledFindingTypes.restorative_adjacent_lucency &&
        (!selectedFinding || selectedFinding?.id === finding?.id) &&
        !hiddenFindings?.some((hidden) => hidden === finding.id),
    )
    .map((filteredFinding: BoundingBoxFinding) => filteredFinding?.toothNumber);

  const initRalFindingsByTooth: {
    tooth?: string | number;
    lesions: number;
  }[] = [];

  const ralFindingsByTooth = ralFindings
    ?.reduce((byTooth, finding) => {
      const tooth = byTooth.find((x) => x?.tooth === finding);
      tooth ? tooth.lesions++ : byTooth.push({ tooth: finding, lesions: 1 });
      return byTooth;
    }, initRalFindingsByTooth)
    .filter(
      (tooth) =>
        !selectedTeeth.length ||
        selectedFinding ||
        selectedTeeth.some((x) => x === tooth.tooth?.toString()),
    )
    .sort((a, b) => sortByTooth(a.tooth, b.tooth));

  return ralFindingsByTooth;
};

export const useGetAnnotationsNearTools = (
  dicomCanvas: HTMLDivElement | null,
  activeImage: ImageObject,
  cornerstoneIsReady: boolean,
) => {
  const setSelectedAnnotation = useSetAtom(selectedAnnotationAtom);
  const annotateAction = useAtomValue(annotateActionAtom);

  const { trackEvent } = useTrackEvent();

  const toolGroup =
    cornerstoneIsReady && ToolGroupManager.getToolGroup(toolGroupId);
  const getAnnotationsNearTools = useCallback(
    (e: any) => {
      if (annotateAction) {
        return;
      }
      // These are all the valid annotation tools we need to sift through
      const validAnnotationTools = [
        PlanarFreehandROITool.toolName,
        LengthTool.toolName,
        ProbeTool.toolName,
        RectangleROITool.toolName,
        EllipticalROITool.toolName,
      ];
      const currentPoints = e.detail.currentPoints;
      if (!toolGroup) {
        return;
      }
      const tools = toolGroup._toolInstances;
      let closestAnnotation: string = '';

      // Run up to 5 times for each of 10-50 radii until we find the single closest annotation
      for (let index = 10; index <= 50 && !closestAnnotation; index += 10) {
        validAnnotationTools.forEach((tool) => {
          const toolName = tool;
          const toolInstance = tools[toolName];

          if (
            !dicomCanvas ||
            typeof toolInstance.isPointNearTool !== 'function' ||
            typeof toolInstance.filterInteractableAnnotationsForElement !==
              'function' ||
            closestAnnotation
          ) {
            return;
          }
          const annotations = getAnnotations(toolName, dicomCanvas);
          if (!annotations?.length) {
            return;
          }

          annotations.forEach((annotation) => {
            if (
              toolInstance.isPointNearTool(
                dicomCanvas,
                annotation,
                currentPoints.canvas,
                index,
                'Mouse',
              ) &&
              annotation.annotationUID &&
              annotation.annotationUID.includes('//') &&
              annotation.annotationUID.split('//')[0]
            ) {
              closestAnnotation = annotation.annotationUID.split('//')[0];
              setSelectedAnnotation({
                selectedAnnotation: closestAnnotation,
                coordinates: currentPoints.page,
                activeImage,
              });
              return;
            }
          });
        });
      }
      if (!closestAnnotation) {
        setSelectedAnnotation({ selectedAnnotation: '' });
      }
      if (closestAnnotation) {
        trackEvent(mixpanelEvents.casePresentationSelectFinding, {
          annotationUuid: closestAnnotation,
          patientId: activeImage.patientInfo.id,
          detectedImageType: activeImage.detectedImageType,
          estimatedVisitId: activeImage.estimatedVisitId,
          image: activeImage.imageId,
        });
      }
    },
    [
      activeImage,
      annotateAction,
      dicomCanvas,
      setSelectedAnnotation,
      toolGroup,
      trackEvent,
    ],
  );

  useEffect(() => {
    if (dicomCanvas && activeImage.imageId && cornerstoneIsReady) {
      dicomCanvas.addEventListener(Events.MOUSE_DOWN, getAnnotationsNearTools);
    }
    return () => {
      dicomCanvas?.removeEventListener(
        Events.MOUSE_DOWN,
        getAnnotationsNearTools,
      );
    };
  }, [
    activeImage.imageId,
    cornerstoneIsReady,
    dicomCanvas,
    getAnnotationsNearTools,
  ]);
};
